import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HeartPulse, Bell, Upload, Trash2, Loader2, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 3 * 1024 * 1024;

// Admin card for uploading the two ECG monitor sounds (looped heartbeat +
// critical alert). Files land in `company_settings.ecg_*_mp3` so every
// DEO's browser fetches the same file from `/api/settings/ecg-sound`.
export default function EcgSoundAdminCard() {
  const [loop, setLoop]     = useState({ data_url: null, name: null });
  const [alrt, setAlrt]     = useState({ data_url: null, name: null });
  const [busy, setBusy]     = useState(null);   // 'loop' | 'alert' | null
  const loopRef  = useRef(null);
  const alertRef = useRef(null);
  const previewRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/ecg-sound`,
        { withCredentials: true });
      setLoop(data?.loop || { data_url: null });
      setAlrt(data?.alert || { data_url: null });
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const upload = async (slot, file) => {
    if (!file) return;
    if (!file.type.startsWith('audio')) return toast.error('Please pick an audio file');
    if (file.size > MAX_BYTES)         return toast.error(`Max ${MAX_BYTES / 1024 / 1024} MB`);

    setBusy(slot);
    try {
      const dataUrl = await new Promise((ok, ko) => {
        const r = new FileReader();
        r.onload  = () => ok(String(r.result));
        r.onerror = () => ko(r.error);
        r.readAsDataURL(file);
      });
      await axios.put(`${API_URL}/api/settings/ecg-sound`,
        { slot, data_url: dataUrl, name: file.name },
        { withCredentials: true });
      toast.success(`${slot === 'loop' ? 'Heartbeat' : 'Alert'} sound saved · ${file.name}`);
      // Nudge every EcgHeartbeat panel in the app to re-fetch
      window.dispatchEvent(new CustomEvent('manha:ecg-sound-updated'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(null);
    }
  };

  const clear = async (slot) => {
    setBusy(slot);
    try {
      await axios.put(`${API_URL}/api/settings/ecg-sound`,
        { slot, data_url: null, name: null },
        { withCredentials: true });
      toast.info(`${slot === 'loop' ? 'Heartbeat' : 'Alert'} sound cleared`);
      window.dispatchEvent(new CustomEvent('manha:ecg-sound-updated'));
      load();
    } catch { toast.error('Clear failed'); }
    finally { setBusy(null); }
  };

  const preview = (dataUrl) => {
    try {
      if (previewRef.current) { previewRef.current.pause(); previewRef.current = null; }
      const a = new Audio(dataUrl);
      a.volume = 0.7;
      a.play().catch(() => toast.warning('Click anywhere first, then Preview'));
      previewRef.current = a;
    } catch {}
  };

  const Row = ({ slot, label, hint, icon: Icon, value, inputRef }) => (
    <div className="flex items-center justify-between gap-3 rounded-sm border border-slate-800 p-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-white font-medium text-sm">
          <Icon className="h-4 w-4 text-slate-400" /> {label}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {value?.data_url
            ? <span className="text-emerald-400">Loaded · {value.name || 'unnamed.mp3'}</span>
            : hint}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {value?.data_url && (
          <Button variant="ghost" size="sm" onClick={() => preview(value.data_url)}
            data-testid={`ecg-admin-${slot}-preview`}
            className="text-emerald-300 hover:bg-emerald-500/10">
            <PlayCircle className="h-4 w-4" />
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}
          disabled={busy === slot}
          data-testid={`ecg-admin-${slot}-upload`}
          className="border-slate-700 text-slate-200">
          {busy === slot ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
          Upload
        </Button>
        {value?.data_url && (
          <Button variant="ghost" size="sm" onClick={() => clear(slot)}
            disabled={busy === slot}
            data-testid={`ecg-admin-${slot}-clear`}
            className="text-rose-300 hover:bg-rose-500/10">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <input ref={inputRef} type="file" accept="audio/*" className="hidden"
          onChange={(e) => upload(slot, e.target.files?.[0])}
          data-testid={`ecg-admin-${slot}-input`} />
      </div>
    </div>
  );

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="ecg-admin-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <HeartPulse className="h-5 w-5 text-rose-400" /> ECG Monitor Sounds
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Admin-managed. Loaded system-wide by every DEO's ECG panel.
          Max 3 MB per file.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Row slot="loop"  label="Heartbeat Loop"
          hint="Looped MP3 played with the ECG animation"
          icon={HeartPulse} value={loop}  inputRef={loopRef} />
        <Row slot="alert" label="Critical Alert"
          hint="Louder MP3 played on security / system alerts"
          icon={Bell}       value={alrt}  inputRef={alertRef} />
      </CardContent>
    </Card>
  );
}
